import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components";
import { Dialog } from "@/components/ui/dialog";
import { X } from "lucide-react";
import toast from "react-hot-toast";
import {
  AgentList,
  AgentDetail,
  CreateAgentsForm,
} from "@/features/agent/components";
import { agentService } from "@/features/agent/services/agent.service";
import type {
  Agent,
  AgentStatus,
  CompanyAgentStatus,
  CreateAgentsParams,
  AgentAttributes,
} from "@/features/agent/interfaces";

type ViewMode = "list" | "create" | "detail";

const AGENTS_QUERY_KEY = ["agents"];

export const Agents: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [selectedAgentId, setSelectedAgentId] = useState<number | null>(null);
  const [agentToDelete, setAgentToDelete] = useState<{ id: number; name: string } | null>(null);
  const queryClient = useQueryClient();

  // Fetch all agents
  const {
    data: agents = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: AGENTS_QUERY_KEY,
    queryFn: () => agentService.getAllAgents(),
  });

  // Fetch single agent for detail view (when we have a selected id)
  const { data: agentDetail } = useQuery({
    queryKey: ["agents", selectedAgentId],
    queryFn: () => agentService.getAgentById(selectedAgentId!),
    enabled: !!selectedAgentId && viewMode === "detail",
  });

  const displayAgent = agentDetail ?? selectedAgent;

  // Create agents mutation
  const createMutation = useMutation({
    mutationFn: (params: CreateAgentsParams) =>
      agentService.createAgents(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AGENTS_QUERY_KEY });
      toast.success("Agents created successfully");
      setViewMode("list");
    },
    onError: (error: unknown) => {
      toast.error(
        typeof error === "string" ? error : "Failed to create agents"
      );
    },
  });

  // Activate agent mutation (status change)
  const activateMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: CompanyAgentStatus }) =>
      agentService.activateAgent({ id, status }),
    onSuccess: (updatedAgent) => {
      queryClient.invalidateQueries({ queryKey: AGENTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ["agents", updatedAgent.id] });
      toast.success("Agent status updated");
      setSelectedAgent(updatedAgent);
    },
    onError: (error: unknown) => {
      toast.error(
        typeof error === "string" ? error : "Failed to update agent status"
      );
    },
  });

  // Update agent mutation (name and attributes)
  const updateMutation = useMutation({
    mutationFn: ({ 
      id, 
      name, 
      attributes,
      regenerateImage
    }: { 
      id: number; 
      name?: string; 
      attributes?: Partial<AgentAttributes>;
      regenerateImage?: boolean;
    }) =>
      agentService.updateAgent({ id, name, attributes, regenerateImage }),
    onSuccess: (updatedAgent, variables) => {
      queryClient.invalidateQueries({ queryKey: AGENTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ["agents", updatedAgent.id] });
      
      // Dismiss loading toast and show success
      if (variables.regenerateImage) {
        toast.success("Agent updated successfully! New images have been generated.", {
          id: "agent-update",
        });
      } else {
        toast.dismiss("agent-update");
        toast.success("Agent updated successfully");
      }
      
      setSelectedAgent(updatedAgent);
    },
    onError: (error: unknown) => {
      // Dismiss loading toast and show error
      toast.dismiss("agent-update");
      toast.error(
        typeof error === "string" ? error : "Failed to update agent"
      );
    },
  });

  // Delete agent mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => agentService.deleteAgent({ id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AGENTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ["agents", selectedAgentId] });
      toast.success("Agent deleted successfully");
      setViewMode("list");
      setSelectedAgent(null);
      setSelectedAgentId(null);
      setAgentToDelete(null);
    },
    onError: (error: unknown) => {
      toast.error(
        typeof error === "string" ? error : "Failed to delete agent"
      );
    },
  });

  const handleCreateNew = () => {
    setViewMode("create");
  };

  const handleEdit = (agent: Agent) => {
    setSelectedAgent(agent);
    setSelectedAgentId(agent.id);
    setViewMode("detail");
  };

  const handleDeleteClick = (agentId: number) => {
    const agent = agents.find((a) => a.id === agentId) ?? displayAgent ?? null;
    setAgentToDelete(
      agent ? { id: agent.id, name: agent.name } : { id: agentId, name: "This agent" }
    );
  };

  const handleDeleteConfirm = async () => {
    if (!agentToDelete) return;
    await deleteMutation.mutateAsync(agentToDelete.id);
  };

  const handleView = (agent: Agent) => {
    setSelectedAgent(agent);
    setSelectedAgentId(agent.id);
    setViewMode("detail");
  };

  const handleBack = () => {
    setViewMode("list");
    setSelectedAgent(null);
    setSelectedAgentId(null);
  };

  const handleCreateAgentsSubmit = async (data: CreateAgentsParams) => {
    await createMutation.mutateAsync(data);
  };

  const handleStatusChange = (agent: Agent, status: AgentStatus) => {
    activateMutation.mutate({ id: agent.id, status });
  };

  const handleUpdateAgent = (
    agentId: number,
    name?: string,
    attributes?: Partial<AgentAttributes>
  ) => {
    const willRegenerateImages = !!attributes;
    
    // Show loading toast if regenerating images
    if (willRegenerateImages) {
      toast.loading("Updating agent and regenerating images...", {
        id: "agent-update",
      });
    }
    
    updateMutation.mutate({ 
      id: agentId, 
      name, 
      attributes,
      // Regenerate image if any attributes are updated
      ...(attributes && { regenerateImage: true })
    });
  };

  if (error) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="bg-destructive/10 border border-destructive rounded-lg p-4">
          <p className="text-destructive">
            Failed to load agents. Please try again.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      {viewMode === "list" && (
        <AgentList
          agents={agents}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          onCreateNew={handleCreateNew}
          onView={handleView}
          isLoading={isLoading}
        />
      )}

      {viewMode === "detail" && displayAgent && (
        <AgentDetail
          agent={displayAgent}
          onDelete={handleDeleteClick}
          onBack={handleBack}
          onStatusChange={handleStatusChange}
          onUpdate={handleUpdateAgent}
          isUpdatingStatus={activateMutation.isPending}
          isUpdating={updateMutation.isPending}
        />
      )}

      <Dialog
        open={agentToDelete !== null}
        onOpenChange={(open) => !open && setAgentToDelete(null)}
        title="Delete agent?"
        description={
          agentToDelete
            ? `This will permanently delete "${agentToDelete.name}". This action cannot be undone.`
            : "This action cannot be undone."
        }
        size="sm"
      >
        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={() => setAgentToDelete(null)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={deleteMutation.isPending}
            onClick={handleDeleteConfirm}
          >
            {deleteMutation.isPending ? "Deleting..." : "Delete"}
          </Button>
        </div>
      </Dialog>

      {viewMode === "create" && (
        <div className="max-w-2xl mx-auto w-full">
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <h1 className="text-2xl sm:text-3xl font-bold">Create Agents</h1>
              <Button variant="ghost" size="icon" onClick={handleBack} className="shrink-0 self-end sm:self-auto">
                <X className="h-5 w-5" />
              </Button>
            </div>
            <p className="text-muted-foreground">
              Create new AI agents for your content plan
            </p>
          </div>
          <div className="bg-card border border-input rounded-lg p-4 sm:p-6">
            <CreateAgentsForm
              onSubmit={handleCreateAgentsSubmit}
              onCancel={handleBack}
              isLoading={createMutation.isPending}
            />
          </div>
        </div>
      )}

    </div>
  );
};
